import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { differenceInCalendarDays, getTime, subDays } from 'date-fns';
import { graphql, fetchQuery, type Environment, useRelayEnvironment } from 'react-relay';
import {
	convertToJiraProjectType,
	projectTypeToCanonicalId,
	toProjectType,
} from '@atlassian/jira-common-constants/src/project-types';
import {
	isSupportedEotmProjectType,
	type SupportedEotmProjectType,
} from '@atlassian/jira-end-of-trial-common';
import { CoordinationClient } from '@atlassian/jira-engagement';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container';
import { ModalContext } from '@atlassian/jira-modal-context';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common';
import { editionAwarenessTrialModalEntryPoint } from '@atlassian/jira-navigation-apps-sidebar-edition-awareness-trial-modal/entrypoint';
import {
	fireOperationalAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	src_EndOfTrialMessageSharedGlobalComponentQuery,
	src_EndOfTrialMessageSharedGlobalComponentQuery$variables,
} from '@atlassian/jira-relay/src/__generated__/src_EndOfTrialMessageSharedGlobalComponentQuery.graphql';
import { getEditionForProject, STANDARD_EDITION } from '@atlassian/jira-shared-types';
import { getApplicationKeyForProject } from '@atlassian/jira-shared-types/src/application';
import {
	useCloudId,
	useAppEditions,
	useProductEntitlementDetails,
} from '@atlassian/jira-tenant-context-controller';
import { STARGATE_URL } from './constants';
import { ModalSkeleton } from './ui';
import { getEndOfTrialMessageAudience, getEndOfTrialMessageExperiment } from './utils';

type EndOfTrialMessageData = {
	trialStartTimestamp: number;
	trialEndTimestamp: number;
	preDunningEndTimestamp: number;
};

export const fetchEndOfTrialMessageData = (
	environment: Environment,
	variables: src_EndOfTrialMessageSharedGlobalComponentQuery$variables,
): Promise<EndOfTrialMessageData | null> =>
	fetchQuery<src_EndOfTrialMessageSharedGlobalComponentQuery>(
		environment,
		graphql`
			query src_EndOfTrialMessageSharedGlobalComponentQuery(
				$cloudId: ID!
				$hamsProductKey: String!
			) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: $hamsProductKey) {
						entitlement(where: { AND: [{ inPreDunning: true }] }) {
							offering {
								... on CcpOffering {
									trial {
										lengthDays
									}
								}
							}
							preDunning {
								firstPreDunningEndTimestamp
							}
						}
					}
				}
			}
		`,
		variables,
	)
		.toPromise()
		.then((data) => {
			const tenantContexts = data?.tenantContexts || [];
			const entitlement = tenantContexts[0]?.entitlementInfo?.entitlement;

			const ccpTrialLength = entitlement?.offering?.trial?.lengthDays;
			// firstPreDunningEndTimestamp is returned as microseconds as opposed to milliseconds
			const firstPreDunningEndTimestampMicroseconds =
				entitlement?.preDunning?.firstPreDunningEndTimestamp;

			if (!ccpTrialLength || !firstPreDunningEndTimestampMicroseconds) {
				return null;
			}

			const preDunningEndTimestamp = firstPreDunningEndTimestampMicroseconds / 1000;

			const trialEndTimestamp = getTime(subDays(new Date(preDunningEndTimestamp), 14));
			const trialStartTimestamp = getTime(subDays(new Date(trialEndTimestamp), ccpTrialLength));

			return {
				preDunningEndTimestamp,
				trialEndTimestamp,
				trialStartTimestamp,
			};
		});

const EndOfTrialMessageSharedGlobalComponentImpl = ({
	supportedEotmProjectType,
}: {
	supportedEotmProjectType: SupportedEotmProjectType;
}) => {
	const relayEnvironment = useRelayEnvironment();
	const cloudId = useCloudId();
	const appEditions = useAppEditions();
	const productEntitlementDetails = useProductEntitlementDetails();
	const { openModal } = useContext(ModalContext);

	const productKey = projectTypeToCanonicalId(supportedEotmProjectType);
	const entitlementDetails = productEntitlementDetails?.[productKey];
	const billingSourceSystem = entitlementDetails?.billingSourceSystem || 'HAMS';
	const edition = getEditionForProject(supportedEotmProjectType, appEditions);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		entryPointReferenceSubject,
		entryPointActions: { load },
	} = useEntryPoint(editionAwarenessTrialModalEntryPoint, {
		cloudId,
		productKey,
		jiraProjectType: convertToJiraProjectType(supportedEotmProjectType),
		jiraApplicationKey: getApplicationKeyForProject(supportedEotmProjectType),
	});
	const hasFetchedMessageData = useRef(false);

	const { cohort, fireStatsigExperimentExposure, flagKey } =
		getEndOfTrialMessageExperiment(supportedEotmProjectType);

	const shouldFetchPreDunning =
		edition === STANDARD_EDITION &&
		billingSourceSystem === 'CCP' &&
		!entitlementDetails?.trialEndTime;

	const coordinationClient = useMemo(
		() => new CoordinationClient(cloudId, STARGATE_URL),
		[cloudId],
	);

	useEffect(() => {
		hasFetchedMessageData.current = false;
	}, [shouldFetchPreDunning]);

	useEffect(() => {
		const fetchPreDunningAndShowModal = async () => {
			// ensure message data is only fetched once as load is new object on each render
			if (!shouldFetchPreDunning || hasFetchedMessageData.current) return;

			hasFetchedMessageData.current = true;

			const endOfTrialMessageData = await fetchEndOfTrialMessageData(relayEnvironment, {
				cloudId,
				hamsProductKey: productKey,
			});

			fireOperationalAnalytics(createAnalyticsEvent({}), 'endOfTrialMessageData fetched', {
				cloudId,
				productKey,
				endOfTrialMessageData,
			});

			if (!endOfTrialMessageData) return;

			const preDunningDaysLeft = differenceInCalendarDays(
				new Date(endOfTrialMessageData.preDunningEndTimestamp),
				new Date(),
			);

			const eotmAudience = getEndOfTrialMessageAudience(
				preDunningDaysLeft,
				supportedEotmProjectType,
			);

			const shouldShowModal = eotmAudience && (await coordinationClient.start(eotmAudience));

			if (!shouldShowModal) return;

			fireStatsigExperimentExposure();
			fireTrackAnalytics(createAnalyticsEvent({}), 'feature exposed', 'endOfTrialMessaging', {
				flagKey,
				entitlementDetails,
				cohort,
				growthInitiative: 'transmuters',
				projectType: supportedEotmProjectType,
				edition,
			});

			if (cohort !== 'variation') return;

			load();
			openModal(({ onClose }) => (
				<JiraEntryPointContainer
					id="endOfTrialMessageModalContainer"
					entryPointReferenceSubject={entryPointReferenceSubject}
					fallback={<ModalSkeleton onClose={onClose} />}
					runtimeProps={{
						onClose,
						projectType: supportedEotmProjectType,
						edition,
						endOfTrialMessageData,
						shouldCloseOnOverlayClick: false,
					}}
				/>
			));

			coordinationClient.stop(eotmAudience);
		};

		fetchPreDunningAndShowModal();
	}, [
		cloudId,
		cohort,
		coordinationClient,
		createAnalyticsEvent,
		edition,
		entryPointReferenceSubject,
		fireStatsigExperimentExposure,
		load,
		openModal,
		productKey,
		supportedEotmProjectType,
		relayEnvironment,
		shouldFetchPreDunning,
		flagKey,
		entitlementDetails,
	]);

	return null;
};

export const EndOfTrialMessageSharedGlobalComponent = () => {
	const { data: project } = useProject();

	const projectType = toProjectType(project?.type);

	if (!isSupportedEotmProjectType(projectType)) {
		return null;
	}

	const { isEnrolled } = getEndOfTrialMessageExperiment(projectType);

	if (!isEnrolled) {
		return null;
	}

	return <EndOfTrialMessageSharedGlobalComponentImpl supportedEotmProjectType={projectType} />;
};
